import React, { useState, useEffect, useRef } from "react";
import {
  Activity,
  Cpu,
  HardDrive,
  Wifi,
  Zap,
  ShieldCheck,
  Terminal,
  Globe,
  Radio,
} from "lucide-react";

/**
 * CinematicDataCounterBackdrop
 * 3D Cinematic, slightly translucent real-time dynamic data counter
 * and telemetry backdrop for RedWORK Webdesign page.
 *
 * Features:
 * - Real-time animated data counter (packets, bytes, throughput, latency)
 * - 3D perspective spatial HUD panels (tilted in 3D isometric planes)
 * - Translucent glassmorphism (15-25% opacity, zero disruption to text)
 * - 3D cyber grid floor & concentric orbital radar data rings
 * - Pure pointer-events-none (100% click-through safety)
 * - Mobile responsive (adapts gracefully from 320px to 4K displays)
 */
// Rolling stream of live hex data packets
const HEX_STREAM = [
  { hash: "0x7F2A:OK", tag: "ZRH-SWISS-01", latency: "0.24ms" },
  { hash: "0x9E10:SYNC", tag: "TLS_1.3_AES256", latency: "0.28ms" },
  { hash: "0x4C88:ACK", tag: "NVME_RAID10", latency: "0.22ms" },
  { hash: "0x11B3:CRYPT", tag: "FINMA_AUDIT", latency: "0.25ms" },
  { hash: "0x6A90:STREAM", tag: "HTTP3_QUIC", latency: "0.29ms" },
  { hash: "0x3D72:VALID", tag: "SWISS_DNS_02", latency: "0.21ms" },
  { hash: "0x88FE:ENCR", tag: "DDOS_FILTER_L7", latency: "0.27ms" },
];

export default function CinematicDataCounterBackdrop() {
  // Live counters state
  const [packetCount, setPacketCount] = useState(14892410);
  const [byteCount, setByteCount] = useState(984210640);
  const [bandwidth, setBandwidth] = useState(48.64);
  const [latency, setLatency] = useState(0.26);
  const [activeRequests, setActiveRequests] = useState(24890);
  const [hexIndex, setHexIndex] = useState(0);

  // Equalizer bar heights (8 bars)
  const [equalizerBars, setEqualizerBars] = useState([
    45, 78, 60, 92, 70, 85, 55, 68,
  ]);

  // High-frequency live data ticker (smooth incrementation)
  useEffect(() => {
    const interval = setInterval(() => {
      // Increment packet counter by random realistic step
      setPacketCount((prev) => prev + Math.floor(Math.random() * 9 + 4));

      // Increment byte counter (roughly ~25-75 KB per tick)
      setByteCount((prev) => prev + Math.floor(Math.random() * 48000 + 12000));

      // Subtle fluctuation for bandwidth (between 47.8 and 49.6 Gbps)
      setBandwidth((prev) => {
        const delta = (Math.random() - 0.5) * 0.18;
        const next = prev + delta;
        return Number(Math.max(47.2, Math.min(50.4, next)).toFixed(2));
      });

      // Subtle fluctuation for latency (between 0.22 and 0.32 ms)
      setLatency((prev) => {
        const delta = (Math.random() - 0.5) * 0.03;
        const next = prev + delta;
        return Number(Math.max(0.21, Math.min(0.33, next)).toFixed(2));
      });

      // Active requests fluctuation
      setActiveRequests((prev) => {
        const delta = Math.floor((Math.random() - 0.5) * 40);
        return Math.max(24500, Math.min(25200, prev + delta));
      });

      // Randomize audio/data equalizer bars
      setEqualizerBars((prev) =>
        prev.map(() => Math.floor(Math.random() * 55 + 35))
      );

      // Rotate hex stream
      setHexIndex((prev) => (prev + 1) % HEX_STREAM.length);
    }, 120);

    return () => clearInterval(interval);
  }, []);

  // Format numbers nicely (Swiss thousand separator: ')
  const formatPackets = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  };

  const formatBytes = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, "'")} MB`;
  };

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none select-none overflow-hidden z-0"
    >
      {/* ================================================== */}
      {/* 1. 3D CYBERNETIC GRID FLOOR (Tilted in 3D Horizon) */}
      {/* ================================================== */}
      <div
        className="absolute inset-x-0 bottom-0 h-[60vh] opacity-15 sm:opacity-20 pointer-events-none"
        style={{
          perspective: "800px",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          className="w-full h-full"
          style={{
            transform: "rotateX(72deg) translateY(20%) scale(1.6)",
            backgroundImage: `
              linear-gradient(to right, rgba(255, 122, 0, 0.15) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(230, 57, 70, 0.15) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse 90% 80% at 50% 100%, #000 60%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 90% 80% at 50% 100%, #000 60%, transparent 100%)",
          }}
        />
      </div>

      {/* ================================================== */}
      {/* 2. 3D CONCENTRIC RADAR RINGS & GLOW CORE          */}
      {/* ================================================== */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none opacity-20 sm:opacity-25">
        {/* Soft Radial Backlight Core */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#E63946]/20 via-[#FF7A00]/10 to-transparent blur-3xl animate-pulse" />

        {/* Outer Ring */}
        <div
          className="absolute inset-4 rounded-full border border-dashed border-[#FF7A00]/25 animate-spin"
          style={{ animationDuration: "90s" }}
        />

        {/* Mid Ring */}
        <div
          className="absolute inset-28 rounded-full border border-white/10"
          style={{
            transform: "rotate(45deg)",
          }}
        />

        {/* Inner Ring with Target Crosshairs */}
        <div
          className="absolute inset-48 rounded-full border border-[#E63946]/30 animate-spin"
          style={{ animationDuration: "45s", animationDirection: "reverse" }}
        />
      </div>

      {/* ================================================== */}
      {/* 3. LEFT 3D FLOATING HUD PANEL (Data Packets & Ping)*/}
      {/* ================================================== */}
      <div
        className="hidden xl:block absolute left-4 2xl:left-8 top-32 w-80 pointer-events-none transition-all duration-700 opacity-20 hover:opacity-40"
        style={{
          perspective: "1200px",
        }}
      >
        <div
          className="p-4 rounded-3xl bg-[#0C101A]/40 backdrop-blur-md border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] font-mono"
          style={{
            transform:
              "rotateY(16deg) rotateX(6deg) translateZ(10px)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-2 mb-3 border-b border-white/10 text-[10px]">
            <div className="flex items-center gap-1.5 text-emerald-400">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
              <span className="font-bold tracking-wider">DATENZÄHLER • LIVE</span>
            </div>
            <span className="text-slate-400">ZRH-NODE-01</span>
          </div>

          {/* Primary Telemetry Metric: Live Packets */}
          <div className="space-y-1 mb-3">
            <span className="text-[9px] uppercase tracking-widest text-slate-400 block font-sans font-bold">
              Pakete in Echtzeit (L3/L4)
            </span>
            <div className="text-2xl font-black text-white tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-[#FF9E40]">
              {formatPackets(packetCount)}
            </div>
            <span className="text-[9px] text-emerald-400/90 font-bold block">
              +128 Pkt/s • Unlimitiert
            </span>
          </div>

          {/* Dynamic Frequency / Throughput Equalizer Bars */}
          <div className="space-y-1.5 mb-3 pt-2 border-t border-white/5">
            <div className="flex items-center justify-between text-[9px] text-slate-400 font-sans">
              <span>Schweizer Durchsatz</span>
              <span className="text-amber-400 font-mono font-bold">{bandwidth} Gbps</span>
            </div>
            <div className="flex items-end gap-1.5 h-7 px-1 bg-black/40 rounded-lg border border-white/5 py-1">
              {equalizerBars.map((height, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm bg-gradient-to-t from-[#E63946] to-[#FF7A00] transition-all duration-150"
                  style={{ height: `${height}%` }}
                />
              ))}
            </div>
          </div>

          {/* Micro Telemetry Pills */}
          <div className="grid grid-cols-2 gap-1.5 text-[9px] pt-1">
            <div className="p-1.5 rounded-lg bg-white/[0.03] border border-white/5">
              <span className="text-slate-500 block">Latenz Zürich</span>
              <span className="font-bold text-white">{latency} ms</span>
            </div>
            <div className="p-1.5 rounded-lg bg-white/[0.03] border border-white/5">
              <span className="text-slate-500 block">Protokoll</span>
              <span className="font-bold text-cyan-400">HTTP/3 QUIC</span>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* 4. RIGHT 3D FLOATING HUD PANEL (Bandwidth & Bytes) */}
      {/* ================================================== */}
      <div
        className="hidden xl:block absolute right-4 2xl:right-8 top-44 w-80 pointer-events-none transition-all duration-700 opacity-20 hover:opacity-40"
        style={{
          perspective: "1200px",
        }}
      >
        <div
          className="p-4 rounded-3xl bg-[#0C101A]/40 backdrop-blur-md border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] font-mono"
          style={{
            transform:
              "rotateY(-16deg) rotateX(6deg) translateZ(10px)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-2 mb-3 border-b border-white/10 text-[10px]">
            <div className="flex items-center gap-1.5 text-[#FF9E40]">
              <Radio size={12} className="animate-pulse" />
              <span className="font-bold tracking-wider">SWISS DATA STREAM</span>
            </div>
            <span className="text-emerald-400 font-bold">99.998% SLA</span>
          </div>

          {/* Primary Telemetry Metric: Total Processed Bytes */}
          <div className="space-y-1 mb-3">
            <span className="text-[9px] uppercase tracking-widest text-slate-400 block font-sans font-bold">
              Verarbeitetes Datenvolumen
            </span>
            <div className="text-2xl font-black text-white tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-[#E63946]">
              {formatBytes(byteCount)}
            </div>
            <span className="text-[9px] text-[#D4AF37] font-bold block">
              Samsung NVMe Gen4 Enterprise
            </span>
          </div>

          {/* Live Rotating Hex Data Packet */}
          <div className="p-2 rounded-xl bg-black/50 border border-white/5 space-y-1 my-3">
            <div className="flex items-center justify-between text-[9px] text-slate-400">
              <span className="flex items-center gap-1 text-cyan-400 font-bold">
                <Terminal size={10} />
                <span>PACKET_STREAM</span>
              </span>
              <span className="text-slate-500 font-mono">
                {HEX_STREAM[hexIndex].latency}
              </span>
            </div>
            <div className="flex items-center justify-between text-[10px]">
              <span className="font-bold text-white">
                {HEX_STREAM[hexIndex].hash}
              </span>
              <span className="text-slate-400 text-[9px]">
                {HEX_STREAM[hexIndex].tag}
              </span>
            </div>
          </div>

          {/* Micro Telemetry Pills */}
          <div className="grid grid-cols-2 gap-1.5 text-[9px] pt-1">
            <div className="p-1.5 rounded-lg bg-white/[0.03] border border-white/5">
              <span className="text-slate-500 block">Sicherheit</span>
              <span className="font-bold text-emerald-400">nDSG / FINMA</span>
            </div>
            <div className="p-1.5 rounded-lg bg-white/[0.03] border border-white/5">
              <span className="text-slate-500 block">Anfragen/Sek</span>
              <span className="font-bold text-amber-400">
                {activeRequests.toLocaleString("de-CH")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* 5. MOBILE / TABLET TELEMETRY STREAM TICKER         */}
      {/* ================================================== */}
      {/* On mobile and tablet, a sleek horizontal translucent strip runs across */}
      <div className="block xl:hidden absolute top-20 inset-x-2 pointer-events-none opacity-25">
        <div className="mx-auto max-w-md p-2.5 rounded-2xl bg-[#0C101A]/50 backdrop-blur-md border border-white/10 font-mono text-[10px] flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-1.5 text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-bold truncate">LIVE: {formatPackets(packetCount)} PKT</span>
          </div>
          <div className="text-slate-400 text-[9px]">
            {bandwidth} Gbps • {latency}ms
          </div>
          <div className="text-[#FF9E40] font-bold text-[9px]">
            99.998% SLA
          </div>
        </div>
      </div>
    </div>
  );
}
